// ** React Imports
import { useContext, useMemo, memo, useRef } from "react";
import { Link } from "react-router-dom";

// ** Redux
import { useSelector, useDispatch } from "react-redux";
import { openAuthModal } from "store/modules/global-common.slice";

// ** Third Party Components
import HoverVideoPlayer from "components/video/HoverVideoPlayer";

// ** Custom Components
import { Image } from "components/media";
import { StyledMasonryCardWrapperNew } from "./styles/MasonryCardWrapperNew.styled";
import UserMediaSkeletor from "components/user/UserMediaSkeletor";
import useMobileDetect from "hooks/useMobileDetectHook";
import { StyledMediaWrapper } from "../../media/styles";
import { FakeActionButton } from "../FakeActionButton";
import DatePresenter from "components/other/DatePresenter";
import Text from "components/typography/Text";
import Block from "components/layout/Block";
import { StyledOverlay } from "styles/components/Overlay.styled";

// ** Utils & Helpers
import generatePath from "utils/generatePath";
import pseudoFormat from "utils/pseudoFormat";

// ** Third Party Components
import { useInView } from "react-intersection-observer";

// ** Context
import authContext from "contexts/authContext";

// ** Routes
import containers from "containers";

// #####################################################

const MasonryVideoNew = ({
	withoutLazyLoading = false,
	contentId,
	contentHeight,
	contentWidth,
	contentAlbum,
	contentType = "video",
	contentPreviewVertical,
	contentSourceFull,
	contentPosterVertical,
	contentLikesCount = 0,
	contentCommentsCount = 0,
	userId,
	userLogin,
	userAvatar,
	userOnline,
	userStatusVip,
	contentData,
	isUserGallery,
	locationSearch = "",
}) => {
	const isAuth = useContext(authContext);

	const to = useMemo(() => {
		if (isAuth) {
			const generatedPath = generatePath(containers.modal.path, {
				contentId,
				login: userLogin?.toLowerCase(),
				type: contentType,
				album: contentAlbum,
			}).toLowerCase();
			return { hash: generatedPath, search: locationSearch };
		}
		return "/";
	}, [isAuth, contentId, userLogin, containers.modal.path]);

	const inlineStyledMemonized = useMemo(() => {
		return { width: "100%", position: "relative" };
	}, []);

	const mobileDetect = useMobileDetect();
	const isIos = mobileDetect.isIos();

	const { ref, inView } = useInView({
		threshold: 0,
		triggerOnce: false,
		rootMargin: "100px 0px 100px 0px",
		initialInView: true,
	});

	const containerRef = useRef(null);
	const lastHeightRef = useRef(null);

	const updateHeight = () => {
		if (containerRef.current) {
			const currentHeight = containerRef.current.offsetHeight;
			if (lastHeightRef.current !== currentHeight) {
				lastHeightRef.current = currentHeight;
			}
		}
	};

	if (inView) {
		updateHeight();
	}

	const combineRef = (el) => {
		containerRef.current = el;
		ref(el);
	};

	const memoizedUser = useMemo(() => {
		return {
			id: userId,
			avatar: userAvatar,
			login: userLogin,
			status_vip: userStatusVip,
			online: userOnline,
		};
	}, []);

	// A/B Test - AuthModal
	const withAuthModal = useSelector(
		(state) => state.globalCommon.withAuthModal
	);
	const dispatch = useDispatch();

	const likesUnderMasonryRender = () => {
		if (isUserGallery) {
			if (withAuthModal && !isAuth) {
				return (
					<Block
						style={inlineStyledMemonized}
						onClick={() => dispatch(openAuthModal())}
					>
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								margin: "0.5rem 0.75rem",
								justifyContent: "space-between",
								width: "calc(100% - 1.5rem)",
							}}
						>
							<Block
								mr={1}
								style={{
									marginTop: "-1px",
									position: "relative",
								}}
							>
								<Text
									color={"grey"}
									style={{ verticalAlign: "bottom" }}
									size={"0.8rem"}
								>
									<DatePresenter
										dateOnly
										date={contentData}
									/>
								</Text>
							</Block>
							<Block flex row alignCenter textNowrap>
								<FakeActionButton
									isUserGalleryProfile
									isHeart
									label={
										contentLikesCount > 0
											? pseudoFormat(contentLikesCount)
											: null
									}
									labelSize="0.8rem"
									customSvgSize="11px"
									customMargin="0 0 0 0"
									customMarginLeft="2px"
									customPadding="0"
								/>
							</Block>
						</div>
					</Block>
				);
			} else {
				return (
					<Link
						to={to}
						style={inlineStyledMemonized}
						onClick={() => !isAuth && window.scrollTo(0, 0)}
					>
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								margin: "0.5rem 0.75rem",
								justifyContent: "space-between",
								width: "calc(100% - 1.5rem)",
							}}
						>
							<Block
								mr={1}
								style={{
									marginTop: "-1px",
									position: "relative",
								}}
							>
								<Text
									color={"grey"}
									style={{ verticalAlign: "bottom" }}
									size={"0.8rem"}
								>
									<DatePresenter
										dateOnly
										date={contentData}
									/>
								</Text>
							</Block>
							<Block flex row alignCenter textNowrap>
								<FakeActionButton
									isUserGalleryProfile
									isHeart
									label={
										contentLikesCount > 0
											? pseudoFormat(contentLikesCount)
											: null
									}
									labelSize="0.8rem"
									customSvgSize="11px"
									customMargin="0 0 0 0"
									customMarginLeft="2px"
									customPadding="0"
								/>
							</Block>
						</div>
					</Link>
				);
			}
		}

		return (
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					margin: "0.5rem 0.75rem",
				}}
			>
				<FakeActionButton
					isHeart
					label={
						contentLikesCount > 0
							? pseudoFormat(contentLikesCount)
							: null
					}
					labelSize="0.8rem"
					customSvgSize="15px"
					customMargin="0 1rem 0 0"
					customPadding="0"
				/>
				<FakeActionButton
					isComment
					labelSize="0.8rem"
					label={
						contentCommentsCount > 0
							? pseudoFormat(contentCommentsCount)
							: null
					}
					customSvgSize="15px"
					customMargin="0 0 0 0"
					customPadding="0"
				/>
			</div>
		);
	};

	// #####################################################

	return (
		<StyledMasonryCardWrapperNew
			className="noselect"
			ref={combineRef}
			style={{
				minHeight: inView
					? "auto"
					: lastHeightRef.current > 0
					? lastHeightRef.current
					: "100px",
			}}
		>
			{inView && withAuthModal && !isAuth ? (
				<Block
					style={inlineStyledMemonized}
					onClick={() => {
						dispatch(openAuthModal());
					}}
				>
					{!isUserGallery && (
						<div
							style={{
								margin: "0.5rem 0.75rem",
								width: "calc(100% - 1.5rem)",
							}}
						>
							<UserMediaSkeletor
								alignCenter
								noMarginBottom
								user={memoizedUser}
								size="xxxsmall"
								vipProps={{
									height: 11.2,
									marginBottom: 0.125,
								}}
								gapAvatarLogin={2}
								noOnlineCircle
							/>
						</div>
					)}

					<StyledMediaWrapper>
						<div style={{ zIndex: 0 }}>
							<HoverVideoPlayer
								videoSrc={
									<>
										{contentPreviewVertical && (
											<source
												src={contentPreviewVertical}
												type="video/webm"
											/>
										)}
										{contentSourceFull && (
											<source
												src={contentSourceFull}
												type="video/mp4"
											/>
										)}
									</>
								}
								playbackStartDelay={100}
								playbackRangeEnd={isIos ? 5 : undefined}
								unloadVideoOnPaused={true}
								preload="none"
								loop
								sizingMode="overlay"
								style={{
									/* MARCIN_P: DZ-1931 - "display:initial" sprawiło, że na Firefoxie na Masonry nie było podglądu filmiku tylko białe tło, prawdopodobnie odczytywało tę właściwość jako "display:inline". 
										Bez display występowały problemy z przerwą między loginem a filmikiem,  dodanie "display:block" pomogło. */
									display: "block",
									overflow: "hidden",
									width: "100%",
								}}
								videoStyle={{
									filter: `${!isAuth ? "blur(7px)" : "none"}`,
								}}
								hoverOverlay={<StyledOverlay />}
								pausedOverlay={
									<Image
										thumbnail={contentPosterVertical}
										alt=""
										width={contentWidth}
										height={contentHeight}
										isMasonry
										withoutLazyLoading={withoutLazyLoading}
										withPlayIcon
										isCover
										withRatio_4_3
										iconSize="masonry"
									/>
								}
							/>
						</div>
					</StyledMediaWrapper>

					{likesUnderMasonryRender()}
				</Block>
			) : inView ? (
				<Link
					to={to}
					style={inlineStyledMemonized}
					onClick={() => !isAuth && window.scrollTo(0, 0)}
				>
					{!isUserGallery && (
						<div
							style={{
								margin: "0.5rem 0.75rem",
								width: "calc(100% - 1.5rem)",
							}}
						>
							<UserMediaSkeletor
								alignCenter
								noMarginBottom
								user={memoizedUser}
								size="xxxsmall"
								vipProps={{
									height: 11.2,
									marginBottom: 0.125,
								}}
								gapAvatarLogin={2}
								noOnlineCircle
							/>
						</div>
					)}

					<StyledMediaWrapper>
						<div style={{ zIndex: 0 }}>
							<HoverVideoPlayer
								videoSrc={
									<>
										{contentPreviewVertical && (
											<source
												src={contentPreviewVertical}
												type="video/webm"
											/>
										)}
										{contentSourceFull && (
											<source
												src={contentSourceFull}
												type="video/mp4"
											/>
										)}
									</>
								}
								playbackStartDelay={100}
								playbackRangeEnd={isIos ? 5 : undefined}
								unloadVideoOnPaused={true}
								preload="none"
								loop
								sizingMode="overlay"
								style={{
									/* MARCIN_P: DZ-1931 - "display:initial" sprawiło, że na Firefoxie na Masonry nie było podglądu filmiku tylko białe tło, prawdopodobnie odczytywało tę właściwość jako "display:inline". 
										Bez display występowały problemy z przerwą między loginem a filmikiem,  dodanie "display:block" pomogło. */
									display: "block",
									overflow: "hidden",
									width: "100%",
								}}
								videoStyle={{
									filter: `${!isAuth ? "blur(7px)" : "none"}`,
								}}
								hoverOverlay={<StyledOverlay />}
								pausedOverlay={
									<Image
										thumbnail={contentPosterVertical}
										alt=""
										width={contentWidth}
										height={contentHeight}
										isMasonry
										withoutLazyLoading={withoutLazyLoading}
										withPlayIcon
										isCover
										withRatio_4_3
										iconSize="masonry"
									/>
								}
							/>
						</div>
					</StyledMediaWrapper>

					{likesUnderMasonryRender()}
				</Link>
			) : null}
		</StyledMasonryCardWrapperNew>
	);
};

export default memo(MasonryVideoNew);
